import { type UserQuizQuestionAnswerDto, type UserQuizStateDto } from '@/api/generated';
import { QueryKeyEnum } from '@/api/model/QueryKey.enum.ts';
import { useApiClient } from '@/api/util/useApiClient.util.ts';
import { useUserContext } from '@/utils/providers/userContext/useUserContext.tsx';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

interface Req {
  userId: string;
  userQuizId?: string;
  userQuizQuestionAnswerDto: UserQuizQuestionAnswerDto;
}

type Response = UserQuizStateDto | undefined;

interface Return {
  answeringError: AxiosError | null;
  answerQuiz: (req: Req) => Promise<Response>;
  isAnswering: boolean;
  userQuizState: Response;
}

export const useAnswerQuizQuestion = (): Return => {
  const { restUserQuizAnswerApi } = useApiClient();
  const { user } = useUserContext();
  const queryClient = useQueryClient();

  const { data, error, isPending, mutateAsync } = useMutation<Response, AxiosError, Req>({
    mutationFn: async req => {
      if (!req.userQuizId) {
        return;
      }
      const response = await restUserQuizAnswerApi.answerUserQuizQuestion(
        req.userId,
        req.userQuizId,
        req.userQuizQuestionAnswerDto,
      );
      return response.data;
    },
    mutationKey: [QueryKeyEnum.ANSWER_QUESTION, user?.userId],
    onSuccess: async (data, variables) => {
      const currentQuestionIndex =
        data?.currentQuestionIndex === null || data?.currentQuestionIndex === undefined
          ? null
          : data?.currentQuestionIndex;

      await queryClient.refetchQueries({
        queryKey: [QueryKeyEnum.GET_QUIZ_STATE, variables?.userId, variables.userQuizId],
      });

      if (currentQuestionIndex && currentQuestionIndex >= 0) {
        await queryClient.refetchQueries({
          queryKey: [QueryKeyEnum.PROCESS, variables?.userId],
        });
        await queryClient.refetchQueries({
          queryKey: [QueryKeyEnum.GET_QUIZ_QUESTION, variables?.userId, variables.userQuizId],
        });
      }
    },
  });

  return {
    answeringError: error,
    answerQuiz: mutateAsync,
    isAnswering: isPending,
    userQuizState: data,
  };
};
